import { ReactNode, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import { filterEmptyValues } from 'utils/helpers'
import { startOfDayUtc, endOfDayUtc, getOnlyDate } from 'utils/helpers/date'
import {
  AggregatorDropdown,
  Button,
  Dropdown,
  MultiSelectDropdown,
  MultiSelectStoreDropdown,
  TextInput,
} from 'components/common'
import { ORDER_STATUS, ORDER_TABLE_FIELDS } from 'utils/constants'
import { CustomObject } from 'types/common'
import { FILTER_FIELD_ORDERS } from './utils'
import { DateTime } from 'components/common/input/dateTime'

const {
  ORDER_NO,
  ORDER_SHORTCODE,
  AGGREGATOR_CODE,
  INVOICE_NO,
  STORE_CODE,
  ERP_SYNC,
  LAST_MILE_SYNC,
  AREA,
  PREPARED_BY,
  STATUS,
  ON_HOLD_ORDER,
  KDS_ELIGIBLE,
  IS_DRAFT,
  IS_POS_ORDER,
  IS_GRUBTECH_ORDER,
  KDS_SYNCED,
  POWER_BI_SYNCED,
  LAST_MILE,
  DRIVER_ID,
} = ORDER_TABLE_FIELDS

const YES_NO_OPTIONS = [
  { label: '--- NONE ---', value: 'NONE', disabled: false },
  { label: 'Yes', value: 'true', disabled: false },
  { label: 'No', value: 'false', disabled: false },
]

const NONE_CLEARABLE_FIELDS = [
  'AGGREGATOR_CODE',
  'ERP_SYNC',
  'LAST_MILE_SYNC',
  'KDS_ELIGIBLE',
  'IS_DRAFT',
  'IS_POS_ORDER',
  'IS_GRUBTECH_ORDER',
  'KDS_SYNCED',
  'POWER_BI_SYNCED',
  'ON_HOLD_ORDER',
] as const

const DATE_RANGE_FIELDS = [
  {
    fromName: 'createdAtFrom',
    toName: 'createdAtTo',
    label: 'Order Posting Date',
  },
  {
    fromName: 'orderPlaceFrom',
    toName: 'orderPlaceTo',
    label: 'Order Place Date',
  },
  {
    fromName: 'plannedDeliveryFrom',
    toName: 'plannedDeliveryTo',
    label: 'Planned Delivery Date',
  },
] as const

type FilterSectionProps = {
  title: string
  children: ReactNode
}

const FilterSection = ({ title, children }: FilterSectionProps) => (
  <div className="border-b border-gray-100 py-4 first:pt-0 last:border-b-0 last:pb-0">
    <p className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-400">
      {title}
    </p>
    {children}
  </div>
)

type Props = {
  toggleFilterForm: () => void
}

const OrderFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_ORDERS)

  const [resetFilter, setResetFilter] = useState(false)

  // Stored date-range values are full UTC ISO instants (see onFilterApply);
  // a native `type="date"` input only accepts yyyy-mm-dd, so convert back to
  // the local calendar date before seeding the form.
  const formDefaultValues = useMemo(() => {
    if (!filters) {
      return filters
    }

    const values = { ...filters }

    DATE_RANGE_FIELDS.forEach(({ fromName, toName }) => {
      if (values[fromName]) {
        values[fromName] = getOnlyDate(values[fromName])
      }

      if (values[toName]) {
        values[toName] = getOnlyDate(values[toName])
      }
    })

    return values
  }, [filters])

  const { register, handleSubmit, setValue, reset } = useForm({
    defaultValues: formDefaultValues,
  })

  const orderStatusOptions = useMemo(() => {
    return Object.values(ORDER_STATUS).map((status) => ({
      label: status,
      value: status,
      disabled: false,
    }))
  }, [])

  const defaultSelectedStatuses = useMemo(() => {
    const status = filters?.[STATUS.value]

    if (Array.isArray(status)) {
      return status
    }

    return typeof status === 'string' && status ? status.split(',') : []
  }, [filters])

  const defaultSelectedStores = useMemo(() => {
    const stores = filters?.[STORE_CODE.value]

    if (Array.isArray(stores)) {
      return stores
    }

    return typeof stores === 'string' && stores ? stores.split(',') : []
  }, [filters])

  const onFilterApply = (data: CustomObject) => {
    NONE_CLEARABLE_FIELDS.forEach((field) => {
      const key = ORDER_TABLE_FIELDS[field].value

      if (data?.[key] === 'NONE') {
        delete data[key]
      }
    })

    // Status and store are multi-select — collapse the arrays into
    // comma-separated strings, since the filter blob is serialized through
    // URLSearchParams, which cannot represent array values.
    if (Array.isArray(data?.[STATUS.value])) {
      data[STATUS.value] = data[STATUS.value].join(',')
    }

    if (Array.isArray(data?.[STORE_CODE.value])) {
      data[STORE_CODE.value] = data[STORE_CODE.value].join(',')
    }

    // Local calendar dates picked in the browser must be converted to UTC
    // instant boundaries so the backend range covers the user's full local day.
    DATE_RANGE_FIELDS.forEach(({ fromName, toName }) => {
      if (data?.[fromName]) {
        data[fromName] = startOfDayUtc(data[fromName])
      }

      if (data?.[toName]) {
        data[toName] = endOfDayUtc(data[toName])
      }
    })

    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({ [FILTER_FIELD_ORDERS]: filteredData, page: 1 })
    } else {
      removeQuery(FILTER_FIELD_ORDERS)
    }

    toggleFilterForm()
  }

  const cancelFilter = () => {
    reset(formDefaultValues)
    setResetFilter((pre) => !pre)
    toggleFilterForm()
  }

  return (
    <div className="mt-2 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <form onSubmit={handleSubmit(onFilterApply)}>
        {/* Order Details */}
        <FilterSection title="Order Details">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-3 lg:grid-cols-4">
            <TextInput
              register={register}
              name={ORDER_NO.value}
              label={ORDER_NO.label}
            />

            <TextInput
              register={register}
              name={ORDER_SHORTCODE.value}
              label={ORDER_SHORTCODE.label}
            />

            <TextInput
              register={register}
              name={INVOICE_NO.value}
              label={INVOICE_NO.label}
            />

            <TextInput
              register={register}
              name={AREA.value}
              label={AREA.label}
            />

            <TextInput
              register={register}
              name={PREPARED_BY.value}
              label={PREPARED_BY.label}
            />

            <AggregatorDropdown
              withNoneOption
              setValue={setValue}
              defaultValue={filters?.[AGGREGATOR_CODE.value] || resetFilter}
              name={AGGREGATOR_CODE.value}
              label={AGGREGATOR_CODE.label}
            />

            <MultiSelectStoreDropdown
              setValue={setValue}
              defaultSelected={defaultSelectedStores}
              name={STORE_CODE.value}
              label={STORE_CODE.label}
              resetForm={resetFilter}
            />

            <MultiSelectDropdown
              name={STATUS.value}
              label={STATUS.label}
              setValue={setValue}
              defaultSelected={defaultSelectedStatuses}
              includeSelectAll
              options={orderStatusOptions}
              reset={resetFilter}
            />

            <Dropdown
              name={ON_HOLD_ORDER.value}
              label={ON_HOLD_ORDER.label}
              setValue={setValue}
              defaultValue={filters?.[ON_HOLD_ORDER.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />

            <Dropdown
              name={IS_DRAFT.value}
              label={IS_DRAFT.label}
              setValue={setValue}
              defaultValue={filters?.[IS_DRAFT.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />
          </div>
        </FilterSection>

        {/* Sync & Source */}
        <FilterSection title="Sync & Source">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-3 lg:grid-cols-4">
            <Dropdown
              name={ERP_SYNC.value}
              label={ERP_SYNC.label}
              setValue={setValue}
              defaultValue={filters?.[ERP_SYNC.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />

            <Dropdown
              name={LAST_MILE_SYNC.value}
              label={LAST_MILE_SYNC.label}
              setValue={setValue}
              defaultValue={filters?.[LAST_MILE_SYNC.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />

            <Dropdown
              name={KDS_ELIGIBLE.value}
              label={KDS_ELIGIBLE.label}
              setValue={setValue}
              defaultValue={filters?.[KDS_ELIGIBLE.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />

            <Dropdown
              name={KDS_SYNCED.value}
              label={KDS_SYNCED.label}
              setValue={setValue}
              defaultValue={filters?.[KDS_SYNCED.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />

            <Dropdown
              name={POWER_BI_SYNCED.value}
              label={POWER_BI_SYNCED.label}
              setValue={setValue}
              defaultValue={filters?.[POWER_BI_SYNCED.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />

            <Dropdown
              name={IS_POS_ORDER.value}
              label={IS_POS_ORDER.label}
              setValue={setValue}
              defaultValue={filters?.[IS_POS_ORDER.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />

            <Dropdown
              name={IS_GRUBTECH_ORDER.value}
              label={IS_GRUBTECH_ORDER.label}
              setValue={setValue}
              defaultValue={filters?.[IS_GRUBTECH_ORDER.value] || resetFilter}
              options={YES_NO_OPTIONS}
            />
          </div>
        </FilterSection>

        {/* Delivery & Fulfillment */}
        <FilterSection title="Delivery & Fulfillment">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-3 lg:grid-cols-4">
            <TextInput
              register={register}
              name={LAST_MILE.value}
              label={LAST_MILE.label}
            />

            <TextInput
              register={register}
              name={DRIVER_ID.value}
              label={DRIVER_ID.label}
            />
          </div>
        </FilterSection>

        {/* Date Ranges */}
        <FilterSection title="Date Ranges">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {DATE_RANGE_FIELDS.map(({ fromName, toName, label }) => (
              <div
                key={fromName}
                className="rounded-lg border border-gray-200 bg-gray-50 p-3"
              >
                <p className="mb-2 text-xs font-semibold text-sidebar-primary">
                  {label}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <DateTime
                    label="From"
                    name={fromName}
                    register={register}
                    parentClassName="bg-white"
                  />

                  <DateTime
                    label="To"
                    name={toName}
                    register={register}
                    parentClassName="bg-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </FilterSection>

        <div className="flex gap-5 pt-4">
          <Button buttonText="Apply Filters" className="w-32" />

          <Button
            buttonText="cancel"
            variant="outline"
            type="button"
            onClick={cancelFilter}
            className="w-32"
          />
        </div>
      </form>
    </div>
  )
}

export default OrderFilterForm
